import React from 'react';
import { Plane, Crown, MapPin, Clock, Shield, Star, Users, Calendar, CheckCircle2 } from 'lucide-react';

const EliteTutoringSection = () => {
  const features = [
    {
      icon: Plane,
      title: 'Worldwide Travel',
      description: 'Dr. Omakobia travels to your location - UK, Europe, or internationally'
    },
    {
      icon: MapPin,
      title: 'Your Preferred Venue',
      description: 'Sessions at your home, holiday residence, yacht, or private location'
    },
    {
      icon: Clock,
      title: 'Flexible Scheduling',
      description: 'Intensive courses, holiday programmes, or regular visits tailored to you'
    },
    {
      icon: Shield,
      title: 'Full Discretion',
      description: 'Complete confidentiality and professionalism for high-profile families'
    }
  ];

  const includes = [
    'Bespoke curriculum design',
    'GCSE/IGCSE Maths & Sciences',
    '11+, 13+ & 7+ entrance exam prep',
    'Intensive revision programmes',
    'Holiday tuition packages',
    'Exam board specific preparation',
    'Progress reports for parents',
    'Direct examiner insights'
  ];

  return (
    <section id="elite-tutoring" style={{ 
      padding: '5rem 1.5rem', 
      background: 'linear-gradient(135deg, #1a1a2e 0%, #0F172A 100%)'
    }}>
      <div className="container">
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'linear-gradient(to right, #C5A059, #E5C575)',
              color: '#1a1a2e',
              padding: '10px 24px',
              borderRadius: '9999px',
              marginBottom: '1.5rem',
              fontSize: '0.875rem',
              fontWeight: 700
            }}>
              <Crown size={18} />
              Elite Service
            </div>
            <h2 style={{ 
              fontSize: '2.5rem', 
              fontWeight: 800, 
              marginBottom: '1rem',
              color: 'white'
            }}>
              Bespoke Travel Tutoring
            </h2>
            <p style={{ 
              color: 'rgba(255,255,255,0.7)', 
              maxWidth: '700px', 
              margin: '0 auto',
              fontSize: '1.1rem',
              lineHeight: 1.7
            }}>
              An exclusive, personalised tutoring service for high-net-worth families. 
              Dr. Michael Omakobia brings world-class GCSE education directly to you, 
              wherever you are in the world.
            </p>
          </div>

          {/* Main Content Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            marginBottom: '3rem'
          }}>
            {/* Left: Features */}
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '20px',
              padding: '2rem',
              border: '1px solid rgba(212, 175, 55, 0.3)'
            }}>
              <h3 style={{ 
                color: '#C5A059', 
                fontSize: '1.25rem', 
                fontWeight: 700, 
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Star size={20} fill="#C5A059" />
                Premium Features
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {features.map((feature, index) => (
                  <div key={index} style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #C5A059 0%, #E5C575 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <feature.icon size={24} color="#1a1a2e" />
                    </div>
                    <div>
                      <h4 style={{ 
                        color: 'white', 
                        fontWeight: 600, 
                        marginBottom: '0.25rem',
                        fontSize: '1rem'
                      }}>
                        {feature.title}
                      </h4>
                      <p style={{ 
                        color: 'rgba(255,255,255,0.6)', 
                        fontSize: '0.9rem',
                        margin: 0,
                        lineHeight: 1.5
                      }}>
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: What's Included */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%)',
              borderRadius: '20px',
              padding: '2rem',
              border: '2px solid #C5A059'
            }}>
              <h3 style={{ 
                color: '#C5A059', 
                fontSize: '1.25rem', 
                fontWeight: 700, 
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <CheckCircle2 size={20} />
                Service Includes
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {includes.map((item, index) => (
                  <div 
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      color: 'white',
                      fontSize: '0.95rem'
                    }}
                  >
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#C5A059',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <CheckCircle2 size={14} color="#1a1a2e" />
                    </div>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pricing & CTA */}
          <div style={{
            background: 'linear-gradient(135deg, #C5A059 0%, #E5C575 100%)',
            borderRadius: '20px',
            padding: '2.5rem',
            textAlign: 'center',
            color: '#1a1a2e'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(0,0,0,0.1)',
              padding: '8px 16px',
              borderRadius: '9999px',
              marginBottom: '1rem',
              fontSize: '0.85rem',
              fontWeight: 600
            }}>
              <Crown size={16} />
              By Appointment Only
            </div>
            <h3 style={{ 
              fontSize: '1.75rem', 
              fontWeight: 800, 
              marginBottom: '0.5rem' 
            }}>
              Enquire About Elite Tutoring
            </h3>
            <p style={{ 
              fontSize: '1rem', 
              opacity: 0.8, 
              marginBottom: '1.5rem',
              maxWidth: '500px',
              margin: '0 auto 1.5rem'
            }}>
              Pricing is bespoke based on location, duration, and requirements. 
              Contact us for a confidential consultation.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href="mailto:drmomakobia@abbaacademy.com?subject=Elite%20Travel%20Tutoring%20Enquiry"
                style={{
                  background: '#1a1a2e',
                  color: '#C5A059',
                  padding: '14px 32px',
                  borderRadius: '9999px',
                  fontWeight: 700,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '1rem'
                }}
              >
                <Crown size={18} />
                Private Enquiry
              </a>
              <a
                href="https://calendly.com/drmomakobia/studentcall"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: 'rgba(0,0,0,0.2)',
                  color: '#1a1a2e',
                  padding: '14px 32px',
                  borderRadius: '9999px',
                  fontWeight: 700,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '1rem'
                }}
              >
                <Calendar size={18} />
                Schedule Call
              </a>
            </div>
          </div>

          {/* Trust Note */}
          <p style={{ 
            textAlign: 'center', 
            color: 'rgba(255,255,255,0.5)', 
            marginTop: '2rem',
            fontSize: '0.9rem'
          }}>
            Trusted by families across the UK & internationally • Full DBS checked • References available
          </p>
        </div>
      </div>
    </section>
  );
};

export default EliteTutoringSection;
